import { FC, ReactNode } from 'react'

import { ContentCopy, Delete, Edit } from '@mui/icons-material'
import { Box, IconButton, IconButtonProps, Tooltip } from '@mui/material'

import { GRTableColumn } from '../../../components/GRTable/GRTable'

type RowActionType = 'edit' | 'clone' | 'delete'

export type RowAction = {
  // Sets the icon, and the error color for delete (primary otherwise); both can be overridden.
  type?: RowActionType
  icon?: ReactNode
  // The tooltip, and the button's accessible name unless `ariaLabel` is given.
  label: string
  ariaLabel?: string
  onClick: () => void
  color?: IconButtonProps['color']
  size?: IconButtonProps['size']
  // When set, the button is wrapped so its tooltip still shows while it is disabled.
  disabled?: boolean
}

const icons: Record<RowActionType, ReactNode> = { edit: <Edit />, clone: <ContentCopy />, delete: <Delete /> }

export const RowActions: FC<{ actions: (RowAction | false | undefined)[] }> = ({ actions }) => (
  <Box display="flex" justifyContent="flex-end">
    {actions.map((action) => {
      if (!action) return null
      const { type, icon, label, ariaLabel, onClick, color, size, disabled } = action
      const button = (
        <IconButton
          size={size}
          aria-label={ariaLabel ?? label}
          color={color ?? (type === 'delete' ? 'error' : 'primary')}
          disabled={disabled}
          onClick={onClick}
        >
          {icon ?? (type && icons[type])}
        </IconButton>
      )
      return (
        <Tooltip key={ariaLabel ?? label} title={label}>
          {disabled === undefined ? button : <span>{button}</span>}
        </Tooltip>
      )
    })}
  </Box>
)

type RowActionsColumnOptions<Row, CustomProps> = {
  actions: (args: { row: Row; rowIndex: number; customTableProps?: CustomProps }) => (RowAction | false | undefined)[]
  width?: number
}

/** The right-aligned, unlabeled last column of internal list tables with the row's icon buttons. */
export const rowActionsColumn = <Row, CustomProps>({ actions, width = 110 }: RowActionsColumnOptions<Row, CustomProps>): GRTableColumn<Row, CustomProps> => ({
  labelAccessor: '',
  accessor: ({ row, rowIndex, customTableProps }) => <RowActions actions={actions({ row, rowIndex, customTableProps })} />,
  cellProps: { align: 'right' },
  headerCellProps: { sx: { width, minWidth: width } },
})
